import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface SearchFilterOption {
  value: string;
  label: string;
  count?: number;
  dotClass?: string;
}

interface SearchFilterBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  options: SearchFilterOption[];
  selected: string;
  onSelect: (value: string) => void;
  children?: ReactNode;
}

export function SearchFilterBar({ value, onChange, placeholder, options, selected, onSelect, children }: SearchFilterBarProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div ref={rootRef} className={`filter-panel relative mobile-panel p-3 ${open ? 'z-50' : 'z-0'}`}>
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder}
            className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm outline-none focus:border-agrida-500"
          />
        </div>
        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border transition-colors ${open || selected ? 'border-agrida-200 bg-agrida-50 text-agrida-700' : 'border-slate-200 bg-white text-slate-500'}`}
          aria-label="Mở bộ lọc"
          aria-expanded={open}
        >
          <i className={`ri-arrow-${open ? 'up' : 'down'}-s-line text-lg`} />
        </button>
      </div>

      {open && (
        <div className="absolute left-3 right-3 top-[64px] z-[70] max-h-[min(62vh,420px)] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_18px_45px_rgba(15,23,42,0.18)]">
          <p className="px-2 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Trạng thái</p>
          {options.map((option) => {
            const active = selected === option.value;
            return (
              <button
                key={option.value || 'all'}
                type="button"
                onClick={() => { onSelect(option.value); setOpen(false); }}
                className={`flex min-h-11 w-full items-center gap-2 rounded-xl px-3 text-left text-sm ${active ? 'bg-agrida-50 text-agrida-700' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <span className={`h-2 w-2 shrink-0 rounded-full ${option.dotClass || (active ? 'bg-agrida-500' : 'bg-slate-300')}`} />
                <span className="min-w-0 flex-1 truncate font-medium">{option.label}</span>
                {option.count !== undefined && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">{option.count}</span>}
                <i className={`ri-check-line text-base ${active ? 'text-agrida-600' : 'invisible'}`} />
              </button>
            );
          })}
          {children && <div className="mt-2 border-t border-slate-100 px-1 pt-2">{children}</div>}
        </div>
      )}
    </div>
  );
}
